import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse, pagedResponseWith } from './pagination.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Attendance (HR-03).
 *
 * A day's status is DERIVED, never stored — the 5b checks in strict order,
 * first match wins (BR-HR-03-01): future → holiday → weekly off → approved
 * leave (BEFORE absence, BR-HR-03-02) → absent → incomplete → half-day /
 * late / present. Everything is computed against the assigned SHIFT
 * (BR-HR-03-03), break deducted from both sides (BR-HR-03-04).
 *
 * THE ONE CORRECTION PATH (owner ruling R1, EC-19-77, BR-PERM-31/32/33):
 * no endpoint in this contract writes a punch directly. Punches come from
 * self check-in/out and from APPROVING a correction request — reason
 * required, hr.approve + routing required (16.5.2b), audit trail permanent.
 * The prototype's direct-write Save does not exist here, for anybody.
 *
 * Weekly off-days: Friday and Saturday (PART 12.1.3's working week) —
 * TODO(branches): per-branch off-days when branches exist as records.
 */
export const DAY_STATUSES = [
    'future',
    'holiday',
    'weekly_off',
    'on_leave',
    'absent',
    'incomplete',
    'half_day',
    'late',
    'present',
];
export const ATTENDANCE_MESSAGES = {
    /** ERR-HR-03-07 / BR-PERM-32's shape. */
    correctionSent: 'Attendance corrections need a manager’s approval. Your request has been sent to a manager.',
    /**
     * BR-HR-03-10 / ERR-HR-03-01 — the reason is required, and the server SAYS
     * so. The prototype's submit button did nothing at all and explained nothing.
     */
    reasonRequired: 'Give the reason for this correction.',
    /** The shared confirmation box's sentence (HR-03 §13). */
    approveConfirm: 'Approving applies the corrected times to the attendance record and closes the regularization request.',
    /** Rejecting requires a comment (HR-03 defect list). */
    rejectCommentRequired: 'Give the reason for rejecting this correction.',
    /** 16.5.2b — routed but the caller is not the routed approver. */
    notRouted: (manager) => `This request is routed to ${manager}. Only they (or the owner) can decide it.`,
    /** BR-HR-04-12's attendance twin — nobody approves their own. */
    ownRequest: 'You cannot decide your own correction request.',
    /** Already decided — decided requests are frozen (BR-HR-03-12). */
    alreadyDecided: 'This request has already been decided.',
    /**
     * Weekend/holiday/future days carry nothing to correct (BR-HR-03-14 /
     * ERR-HR-03-02 — the panel opens, the correction block does not).
     */
    nothingToCorrect: 'There is nothing to correct on a weekend, holiday or future day.',
    /** Self-service double check-in (ERR-HR-03-06's server floor). */
    alreadyCheckedIn: 'You are already checked in today.',
    notCheckedIn: 'Check in before checking out.',
    /** The caller has no employee record to punch against. */
    noEmployeeRecord: 'Your account is not linked to an employee record. Ask HR.',
};
/**
 * D46 — HR-04's attendance-routing refusal, structured. `manager` is a display
 * name. Byte-identical to leave's `notRouted`, but keeps its own key
 * (`hr.errAttendanceNotRouted`) so the census's one-key-per-row rule holds.
 * Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const ATTENDANCE_REFUSALS = {
    notRouted: (manager) => ({
        message: ATTENDANCE_MESSAGES.notRouted(manager),
        key: 'hr.errAttendanceNotRouted',
        params: { manager },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ShiftSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    nameAr: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    breakMinutes: z.number().int(),
    graceMinutes: z.number().int(),
    active: z.boolean(),
})
    .strict();
export const HolidaySchema = z
    .object({ id: z.uuid(), day: z.string(), name: z.string(), nameAr: z.string() })
    .strict();
/** One derived day (HR-03 §5f's six tiles + the square). */
export const DayDetailSchema = z
    .object({
    employeeId: z.uuid(),
    day: z.string(),
    status: z.enum(DAY_STATUSES),
    checkIn: z.string().nullable(),
    checkOut: z.string().nullable(),
    /** Hours worked, 2 decimals; zero on absent/incomplete (BR-HR-03-06). */
    hours: z.string(),
    lateBy: z.number().int(),
    earlyDeparture: z.number().int(),
    overtime: z.string(),
})
    .strict();
export const RegisterRowSchema = z
    .object({
    employeeId: z.uuid(),
    code: z.string(),
    fullName: z.string(),
    fullNameAr: z.string(),
    shiftId: z.uuid().nullable(),
    /** One status per day of the month, index 0 = the 1st. */
    days: z.enum(DAY_STATUSES).array(),
})
    .strict();
/** HR-03 §5e + the defect fix: half-days and incomplete get columns. */
export const SummaryRowSchema = z
    .object({
    employeeId: z.uuid(),
    code: z.string(),
    fullName: z.string(),
    fullNameAr: z.string(),
    shiftName: z.string().nullable(),
    present: z.number().int(),
    absent: z.number().int(),
    late: z.number().int(),
    halfDays: z.number().int(),
    incomplete: z.number().int(),
    leave: z.number().int(),
    overtime: z.string(),
    hours: z.string(),
})
    .strict();
export const CorrectionSchema = z
    .object({
    id: z.uuid(),
    employeeId: z.uuid(),
    employeeName: z.string(),
    day: z.string(),
    originalIn: z.string().nullable(),
    originalOut: z.string().nullable(),
    requestedIn: z.string().nullable(),
    requestedOut: z.string().nullable(),
    reason: z.string(),
    status: z.enum(['pending', 'approved', 'rejected']),
    requestedBy: z.uuid(),
    decidedBy: z.uuid().nullable(),
    decidedAt: z.string().nullable(),
    decisionComment: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
const TimeSchema = z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'HH:MM');
export const SubmitCorrectionRequestSchema = z
    .object({
    /** Omitted = the caller's own employee record (the self-service floor). */
    employeeId: z.uuid().optional(),
    day: IsoDateSchema,
    requestedIn: TimeSchema.optional(),
    requestedOut: TimeSchema.optional(),
    /** BR-HR-03-10: required, server-enforced — not merely a greyed button. */
    reason: z.string().trim().min(1).max(500),
})
    .strict();
/**
 * The month, as a raw shape so the paged reads can spread it beside
 * `page`/`pageSize`. (The paging wave converted the last alone-standing
 * caller, which orphaned the wrapped `MonthQuery` this sat beside — the wrap
 * went with that caller.)
 */
const MonthShape = { month: z.string().regex(/^\d{4}-\d{2}$/, 'YYYY-MM') };
export const attendanceContract = c.router({
    myDay: {
        method: 'GET',
        path: '/api/hr/attendance/me',
        responses: {
            200: z
                .object({
                employee: z
                    .object({ id: z.uuid(), code: z.string(), fullName: z.string(), fullNameAr: z.string() })
                    .strict()
                    .nullable(),
                shift: ShiftSchema.nullable(),
                today: DayDetailSchema.nullable(),
            })
                .strict(),
            401: MessageSchema,
        },
        summary: 'The self-service card (HR-03 §9): your own day — session required, NO role required (rung 4). Null employee = the account is not linked to a staff record.',
    },
    checkIn: {
        method: 'POST',
        path: '/api/hr/attendance/check-in',
        body: z.object({}).strict().optional(),
        responses: { 200: DayDetailSchema, 401: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Record the current clock time as your arrival — own record, rung 4.',
    },
    checkOut: {
        method: 'POST',
        path: '/api/hr/attendance/check-out',
        body: z.object({}).strict().optional(),
        responses: { 200: DayDetailSchema, 401: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Record your departure, preserving the existing arrival (BR-HR-03-15).',
    },
    register: {
        method: 'GET',
        path: '/api/hr/attendance/register',
        query: pagedQuery(MonthShape),
        responses: {
            200: pagedResponseWith('rows', RegisterRowSchema, {
                month: z.string(),
                daysInMonth: z.number().int(),
                holidays: HolidaySchema.array(),
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The month grid (hr.view), PAGED (NFR-42) — the MONTH was always a window (§5d); the ROWS are one per employee and each carries thirty-one derived squares, so they page. Every square DERIVED by the 5b checks in strict order — approved leave before absence (BR-HR-03-02).',
    },
    summary: {
        method: 'GET',
        path: '/api/hr/attendance/summary',
        query: pagedQuery(MonthShape),
        responses: {
            200: pagedResponseWith('rows', SummaryRowSchema, { month: z.string() }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The monthly summary (hr.view), PAGED — payroll’s input: future days skipped, late counts as present too (BR-HR-03-09), half-days and incomplete get their columns (defect fix).',
    },
    dayDetail: {
        method: 'GET',
        path: '/api/hr/attendance/day',
        query: z.object({ employeeId: z.uuid(), date: IsoDateSchema }).strict(),
        responses: {
            200: z
                .object({ detail: DayDetailSchema, shift: ShiftSchema.nullable(), corrections: CorrectionSchema.array() })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The day panel: six tiles + the permanent correction audit trail (BR-HR-03-12). Own day on the rung-4 floor; anybody else’s needs hr.view.',
    },
    submitCorrection: {
        method: 'POST',
        path: '/api/hr/attendance/corrections',
        body: SubmitCorrectionRequestSchema,
        responses: {
            201: z.object({ correction: CorrectionSchema, message: z.string() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'THE one correction path (BR-HR-03-10/-11, BR-PERM-32): reason required, record UNCHANGED until approval. Own day on the rung-4 floor; somebody else’s day needs hr.edit. No endpoint writes a punch directly.',
    },
    listCorrections: {
        method: 'GET',
        path: '/api/hr/attendance/corrections',
        query: pagedQuery({
            status: z.enum(['pending', 'approved', 'rejected']).optional(),
            month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
        }),
        responses: {
            200: pagedResponse('corrections', CorrectionSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Needs-attention queue (hr.view), PAGED (NFR-42) — both filters in SQL, the month included: it is a half-open range on `day`, not a string prefix applied after the page.',
    },
    approveCorrection: {
        method: 'POST',
        path: '/api/hr/attendance/corrections/:correctionId/approve',
        pathParams: z.object({ correctionId: z.uuid() }).strict(),
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ correction: CorrectionSchema, day: DayDetailSchema }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'BOTH gates (BR-PERM-44): hr.approve AND the routed approver — the employee’s manager (or the owner; unrouted requests fall to any holder, BR-PERM-47). Approval is what writes the punch (BR-HR-03-11); who decided is the REAL user (BR-HR-03-13). Nobody decides their own.',
    },
    rejectCorrection: {
        method: 'POST',
        path: '/api/hr/attendance/corrections/:correctionId/reject',
        pathParams: z.object({ correctionId: z.uuid() }).strict(),
        body: z.object({ comment: z.string().trim().min(1).max(500) }).strict(),
        responses: {
            200: CorrectionSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reject with a required comment (defect fix). The record stays untouched.',
    },
    listShifts: {
        method: 'GET',
        path: '/api/hr/shifts',
        responses: {
            200: z.object({ shifts: ShiftSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Shift definitions (hr.view).',
    },
    createShift: {
        method: 'POST',
        path: '/api/hr/shifts',
        body: z
            .object({
            name: z.string().trim().min(1).max(80),
            nameAr: z.string().trim().min(1).max(80).optional(),
            startsAt: TimeSchema,
            endsAt: TimeSchema,
            breakMinutes: z.number().int().min(0).max(600),
            graceMinutes: z.number().int().min(0).max(120),
        })
            .strict(),
        responses: { 201: ShiftSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 409: MessageSchema },
        summary: 'Add a shift (the shift-and-calendar configuration permission). The read-only prototype block is a defect.',
    },
    updateShift: {
        method: 'PATCH',
        path: '/api/hr/shifts/:shiftId',
        pathParams: z.object({ shiftId: z.uuid() }).strict(),
        body: z
            .object({
            name: z.string().trim().min(1).max(80).optional(),
            nameAr: z.string().trim().min(1).max(80).optional(),
            startsAt: TimeSchema.optional(),
            endsAt: TimeSchema.optional(),
            breakMinutes: z.number().int().min(0).max(600).optional(),
            graceMinutes: z.number().int().min(0).max(120).optional(),
            active: z.boolean().optional(),
        })
            .strict(),
        responses: { 200: ShiftSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Edit or deactivate a shift (configuration permission).',
    },
    assignShift: {
        method: 'POST',
        path: '/api/hr/shifts/assign',
        body: z.object({ employeeIds: z.uuid().array().min(1), shiftId: z.uuid() }).strict(),
        responses: {
            200: z.object({ assigned: z.number().int() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Assign a shift per employee or bulk (hr.edit) — "Shift assigned to N employees".',
    },
    listHolidays: {
        method: 'GET',
        path: '/api/hr/holidays',
        responses: {
            200: z.object({ holidays: HolidaySchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The holiday calendar (hr.view).',
    },
    createHoliday: {
        method: 'POST',
        path: '/api/hr/holidays',
        body: z
            .object({
            day: IsoDateSchema,
            name: z.string().trim().min(1).max(120),
            nameAr: z.string().trim().min(1).max(120).optional(),
        })
            .strict(),
        responses: { 201: HolidaySchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 409: MessageSchema },
        summary: 'Add a public holiday (configuration permission) — the list changes every year.',
    },
    deleteHoliday: {
        method: 'DELETE',
        path: '/api/hr/holidays/:holidayId',
        pathParams: z.object({ holidayId: z.uuid() }).strict(),
        responses: { 204: c.noBody(), 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Remove a holiday (configuration permission).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=attendance.js.map